<template>
  <modal
    v-if="isOpen"
    :title="$t('Inventory.send_to_inventory_title')"
    closable
    @close-request="requestClose"
  >
    <form class="send-inventory-form" @submit.prevent="submit">
      <div class="send-inventory-form__toolbar">
        <div>
          <strong>{{ $t('Inventory.selected_vehicles') }}: {{ selectedCount }}</strong>
          <span>{{ $t('Inventory.send_to_inventory_help') }}</span>
        </div>
        <label class="send-inventory-form__select-all">
          <input
            type="checkbox"
            :checked="allEligibleSelected"
            :disabled="submitting || creationCommitted"
            @change="setAllEligible($event.target.checked)"
          >
          {{ $t('Inventory.select_all_eligible') }}
        </label>
      </div>

      <div class="send-inventory-form__settings">
        <label class="form-group">
          <span>{{ $t('Inventory.default_entry_date') }} *</span>
          <input v-model="batchDateInput" class="form-control" type="datetime-local" :disabled="submitting || creationCommitted" required>
        </label>
        <label class="form-group">
          <span>{{ $t('Inventory.dispatch_driver') }} *</span>
          <select
            v-model="driverUID"
            class="form-control"
            :disabled="submitting || creationCommitted"
            required
            @change="driverSelectionTouched = true"
          >
            <option value="">{{ $t('Inventory.choose_driver') }}</option>
            <option v-for="driver in drivers" :key="driverId(driver)" :value="driverId(driver)">
              {{ driverName(driver) }}
            </option>
          </select>
        </label>
      </div>

      <div class="send-inventory-form__bulk">
        <label>
          <span>{{ $t('Inventory.lot') }}</span>
          <select
            v-model="bulkInventoryLot"
            class="form-control"
            :disabled="submitting || creationCommitted"
          >
            <option value="">{{ $t('Inventory.choose_lot') }}</option>
            <option v-for="lot in inventoryLots" :key="lot" :value="lot">{{ lot }}</option>
          </select>
        </label>
        <label>
          <span>{{ $t('Inventory.storage_term') }}</span>
          <select
            v-model="bulkInventoryTerm"
            class="form-control"
            :disabled="submitting || creationCommitted"
          >
            <option value="">{{ $t('Inventory.choose_storage_term') }}</option>
            <option v-for="term in inventoryTerms" :key="term" :value="term">
              {{ inventoryTermLabel(term) }}
            </option>
          </select>
        </label>
        <button
          type="button"
          class="btn btn-light send-inventory-form__bulk-apply"
          :disabled="!canApplyBulkSettings || submitting || creationCommitted"
          @click="applyBulkSettings"
        >
          <i class="fa fa-clone" aria-hidden="true" />
          {{ $t('Inventory.apply_to_selected') }}
        </button>
      </div>

      <div class="send-inventory-form__orders">
        <article
          v-for="row in rows"
          :key="row.orderId"
          class="send-inventory-order"
          :class="{
            'send-inventory-order--selected': row.checked && row.eligible,
            'send-inventory-order--disabled': !row.eligible
          }"
        >
          <label class="send-inventory-order__check">
            <input v-model="row.checked" type="checkbox" :disabled="!row.eligible || submitting || creationCommitted">
            <span class="sr-only">{{ $t('Inventory.select') }} {{ row.orderId }}</span>
          </label>

          <div class="send-inventory-order__image">
            <img v-if="row.image" :src="row.image" :alt="vehicleTitle(row.order)">
            <i v-else class="fa fa-car" aria-hidden="true" />
          </div>

          <div class="send-inventory-order__identity">
            <div>
              <strong>{{ row.orderId }}</strong>
              <span v-if="!row.eligible" class="send-inventory-order__blocked">
                {{ row.order.sentToInventory ? $t('Inventory.already_sent') : $t('Inventory.not_eligible') }}
              </span>
            </div>
            <h4>{{ vehicleTitle(row.order) }}</h4>
            <p>{{ row.order.vin || $t('Inventory.not_provided') }}</p>
            <small>{{ $realName(row.order, 'payer') || '—' }}</small>
            <status-badge v-if="row.order.status" :status="row.order.status" size="sm" />
          </div>

          <div v-if="row.eligible" class="send-inventory-order__controls">
            <label class="send-inventory-order__lot">
              <span>{{ $t('Inventory.lot') }} *</span>
              <select
                v-model="row.inventoryLot"
                class="form-control"
                :disabled="!row.checked || submitting || creationCommitted"
                required
              >
                <option value="">{{ $t('Inventory.choose_lot') }}</option>
                <option v-for="lot in inventoryLots" :key="lot" :value="lot">{{ lot }}</option>
              </select>
            </label>
            <label class="send-inventory-order__term">
              <span>{{ $t('Inventory.storage_term') }} *</span>
              <select
                v-model="row.inventoryTerm"
                class="form-control"
                :disabled="!row.checked || submitting || creationCommitted"
                required
              >
                <option value="">{{ $t('Inventory.choose_storage_term') }}</option>
                <option v-for="term in inventoryTerms" :key="term" :value="term">
                  {{ inventoryTermLabel(term) }}
                </option>
              </select>
            </label>
            <div class="send-inventory-order__date">
              <label>
                <input v-model="row.useDateOverride" type="checkbox" :disabled="!row.checked || submitting || creationCommitted">
                {{ $t('Inventory.custom_entry_date') }}
              </label>
              <input
                v-if="row.useDateOverride"
                v-model="row.dateInput"
                class="form-control"
                type="datetime-local"
                :disabled="!row.checked || submitting || creationCommitted"
              >
              <small v-else>{{ $t('Inventory.uses_default_entry_date') }}</small>
            </div>
          </div>
        </article>
      </div>

      <div v-if="feedback" class="send-inventory-form__message" :class="feedbackClass" role="alert">
        {{ feedback }}
      </div>

      <div v-if="createdOrderIds.length" class="send-inventory-form__created">
        <strong>{{ $t('Inventory.created_orders') }}</strong>
        <span v-for="orderId in createdOrderIds" :key="orderId">{{ orderId }}</span>
      </div>

      <div v-if="failedDispatchResults.length" class="send-inventory-form__retry">
        <span>
          {{ $t('Inventory.dispatch_failed_count') }}: {{ failedDispatchResults.length }}
          ({{ failedDispatchResults.map(result => result.orderId).join(', ') }}).
          {{ $t('Inventory.retry_before_closing') }}
        </span>
        <button type="button" class="btn btn-light" :disabled="submitting" @click="retryFailedDispatches">
          <i class="fa fa-refresh mr-1" aria-hidden="true" />
          {{ $t('Inventory.retry_dispatch') }}
        </button>
      </div>

      <footer>
        <button type="button" class="btn btn-light" :disabled="submitting || failedDispatchResults.length > 0" @click="requestClose">
          {{ $t('Inventory.cancel') }}
        </button>
        <button type="submit" class="btn btn-kne-orange" :disabled="!formValid || submitting || creationCommitted">
          <i :class="['fa', submitting ? 'fa-spinner fa-spin' : 'fa-archive']" aria-hidden="true" />
          {{ submitting ? $t('Inventory.sending_to_inventory') : $t('Inventory.create_inventory_orders') }}
        </button>
      </footer>
    </form>
  </modal>
</template>

<script>
import Modal from '../../interface-components/Modal.vue'
import StatusBadge from '../Other-components/StatusBadge.vue'
import { db } from '../../../../firebase'
import { buildInventoryOrderFromSource } from '../../../../inventory/inventoryOrderFactory'
import {
  canSendToInventory,
  getInventoryImage,
  INVENTORY_TERM,
  INVENTORY_TERMS,
  isDefaultInventoryDriverName,
  isInventoryTerm
} from '../../../../inventory/inventorySelectors'
import { createInventoryBatch, retryInventoryDispatch } from '../../../../inventory/inventoryService'
import { fromTorontoDateTimeInput, toTorontoDateTimeInput } from './inventoryDateTime'

export default {
  name: 'SendToInventoryModal',

  components: {
    Modal,
    StatusBadge
  },

  props: {
    value: {
      type: Boolean,
      default: false
    },
    open: {
      type: Boolean,
      default: true
    },
    orders: {
      type: Array,
      default: () => []
    },
    drivers: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      rows: [],
      inventoryTerms: INVENTORY_TERMS,
      batchDateInput: toTorontoDateTimeInput(),
      driverUID: '',
      driverSelectionTouched: false,
      bulkInventoryLot: '',
      bulkInventoryTerm: '',
      submitting: false,
      creationCommitted: false,
      feedback: '',
      feedbackType: 'error',
      createdOrderIds: [],
      dispatchResults: []
    }
  },

  computed: {
    inventoryLots () {
      return this.$store.getters.inventoryLotNames
    },
    isOpen () {
      return this.open || this.value
    },
    companyUID () {
      return this.$store.state.authorization && this.$store.state.authorization.company
    },
    operator () {
      const user = this.$store.state.user || {}
      const userInfo = this.$store.state.userInfo || {}
      return {
        uid: user.uid || '',
        name: userInfo.name || user.displayName || user.email || ''
      }
    },
    selectedRows () {
      return this.rows.filter(row => row.eligible && row.checked)
    },
    selectedCount () {
      return this.selectedRows.length
    },
    allEligibleSelected () {
      const eligibleRows = this.rows.filter(row => row.eligible)
      return Boolean(eligibleRows.length) && eligibleRows.every(row => row.checked)
    },
    selectedDriver () {
      const driver = this.drivers.find(driver => this.driverId(driver) === this.driverUID)
      return driver ? {uid: this.driverId(driver), name: this.driverName(driver)} : null
    },
    canApplyBulkSettings () {
      return Boolean(
        this.selectedRows.length &&
        this.inventoryLots.indexOf(this.bulkInventoryLot) !== -1 &&
        isInventoryTerm(this.bulkInventoryTerm)
      )
    },
    formValid () {
      const datesValid = this.selectedRows.every(row => {
        return !row.useDateOverride || !isNaN(fromTorontoDateTimeInput(row.dateInput))
      })
      const lotsValid = this.selectedRows.every(row => this.inventoryLots.indexOf(row.inventoryLot) !== -1)
      const termsValid = this.selectedRows.every(row => isInventoryTerm(row.inventoryTerm))
      return Boolean(
        this.companyUID &&
        this.selectedRows.length &&
        this.selectedDriver &&
        !isNaN(fromTorontoDateTimeInput(this.batchDateInput)) &&
        datesValid &&
        lotsValid &&
        termsValid
      )
    },
    failedDispatchResults () {
      return this.dispatchResults.filter(result => !result.queued)
    },
    feedbackClass () {
      return this.feedbackType === 'success'
        ? 'send-inventory-form__message--success'
        : 'send-inventory-form__message--error'
    }
  },

  watch: {
    isOpen (value) {
      if (value) {
        this.reset()
        this.$store.dispatch('loadInventoryLots')
      }
    },
    orders: {
      deep: false,
      handler () {
        if (this.isOpen && !this.creationCommitted) this.buildRows()
      }
    },
    drivers () {
      if (this.isOpen && !this.driverSelectionTouched && !this.driverUID) {
        this.preselectInventoryDriver()
      }
    }
  },

  created () {
    if (this.isOpen) {
      this.reset()
      this.$store.dispatch('loadInventoryLots')
    }
  },

  methods: {
    orderId (order) {
      return String(order['.key'] || order.orderNumber || order.key || '')
    },
    driverId (driver) {
      return String(driver.uid || driver.key || driver['.key'] || '')
    },
    driverName (driver) {
      return driver.name || driver.value || driver['.value'] || ''
    },
    preselectInventoryDriver () {
      if (this.driverSelectionTouched || this.driverUID) return
      const driver = this.drivers.find(driver => isDefaultInventoryDriverName(this.driverName(driver)))
      if (driver) this.driverUID = this.driverId(driver)
    },
    vehicleTitle (order) {
      return [order.year, order.make, order.model, order.trim].filter(Boolean).join(' ') || this.$t('Inventory.vehicle_pending')
    },
    inventoryTermLabel (term) {
      return term === INVENTORY_TERM.LONG_TERM
        ? this.$t('Inventory.long_term')
        : this.$t('Inventory.short_term')
    },
    buildRows () {
      this.rows = this.orders.map(order => ({
        order,
        orderId: this.orderId(order),
        eligible: canSendToInventory(order),
        checked: canSendToInventory(order),
        inventoryLot: '',
        inventoryTerm: '',
        useDateOverride: false,
        dateInput: this.batchDateInput,
        image: getInventoryImage(order, '')
      }))
    },
    setAllEligible (checked) {
      this.rows.forEach(row => {
        if (row.eligible) row.checked = checked
      })
    },
    applyBulkSettings () {
      if (!this.canApplyBulkSettings || this.submitting || this.creationCommitted) return
      this.selectedRows.forEach(row => {
        row.inventoryLot = this.bulkInventoryLot
        row.inventoryTerm = this.bulkInventoryTerm
      })
    },
    fallbackDealerFromOrder (order) {
      return {
        name: order.payerName || '',
        address: order.payerAddress || '',
        city: order.payerCity || '',
        postcode: order.payerPostcode || '',
        province: order.payerProvince || '',
        country: order.payerCountry || '',
        phone: order.payerPhone || '',
        email: order.payerEmail || '',
        fax: order.payerFax || '',
        contacts: order.payerContact || [],
        contact: order.payerContactName || '',
        lat: order.payerLat,
        long: order.payerLong,
        placeId: order.payerPlaceId || '',
        zone: order.payerZone || '',
        zoneId: order.payerZoneId || '',
        sageID: order.sageID || '',
        odooPartnerId: order.payerOdooPartnerId || '',
        odooPartnerName: order.payerOdooPartnerName || '',
        zeroTax: order.zeroTax === true,
        billingComment: order.billingComment || ''
      }
    },
    async loadFreshSourceAndDealer (row) {
      const sourceSnapshot = await db.ref(`database/transporter/${this.companyUID}/deliveries/${row.orderId}`).once('value')
      if (!sourceSnapshot.exists()) throw new Error(`${this.$t('Inventory.source_order_missing')}: ${row.orderId}`)
      const sourceOrder = {...sourceSnapshot.val(), '.key': row.orderId}
      if (!canSendToInventory(sourceOrder)) throw new Error(`${this.$t('Inventory.source_order_ineligible')}: ${row.orderId}`)

      let dealer = this.fallbackDealerFromOrder(sourceOrder)
      if (sourceOrder.payer) {
        try {
          const dealerSnapshot = await db.ref(`users/dealers/${sourceOrder.payer}`).once('value')
          if (dealerSnapshot.exists()) dealer = {...dealer, ...dealerSnapshot.val()}
        } catch (error) {
          // The source order already contains the payer snapshot needed for a safe fallback.
        }
      }
      return {sourceOrder, dealer}
    },
    async submit () {
      if (!this.formValid || this.submitting || this.creationCommitted) return
      this.submitting = true
      this.feedback = ''
      try {
        const rowsToCreate = this.selectedRows.map(row => ({...row}))
        const selectedDriver = {...this.selectedDriver}
        const batchDateInput = this.batchDateInput
        const prepared = []
        for (const row of rowsToCreate) {
          const {sourceOrder, dealer} = await this.loadFreshSourceAndDealer(row)
          const dateEntered = fromTorontoDateTimeInput(row.useDateOverride ? row.dateInput : batchDateInput)
          const order = buildInventoryOrderFromSource({
            sourceOrder,
            sourceOrderId: row.orderId,
            dealer,
            dealerId: sourceOrder.payer || '',
            inventoryLot: row.inventoryLot,
            inventoryTerm: row.inventoryTerm,
            dateEntered,
            operator: this.operator,
            zones: this.$store.state.zones,
            transporter: this.$store.state.userInfo || {}
          })
          prepared.push({order, sourceOrderId: row.orderId})
        }

        const result = await createInventoryBatch({
          companyUID: this.companyUID,
          orders: prepared,
          driver: selectedDriver,
          operator: this.operator
        })
        this.creationCommitted = true
        this.createdOrderIds = result.createdOrderIds || []
        this.dispatchResults = result.dispatchResults || []
        if (result.allDispatchesQueued !== false) {
          this.feedback = this.$t('Inventory.send_success')
          this.feedbackType = 'success'
          this.$emit('completed', result)
          this.requestClose(true)
        } else {
          this.feedback = this.$t('Inventory.created_dispatch_partial_error')
          this.feedbackType = 'error'
          this.$emit('partial', result)
        }
      } catch (error) {
        this.feedback = error.message || this.$t('Inventory.send_error')
        this.feedbackType = 'error'
      } finally {
        this.submitting = false
      }
    },
    async retryFailedDispatches () {
      if (!this.failedDispatchResults.length || !this.selectedDriver) return
      this.submitting = true
      try {
        const retried = await Promise.all(this.failedDispatchResults.map((result, index) => {
          return retryInventoryDispatch({
            companyUID: this.companyUID,
            orderId: result.orderId,
            driver: this.selectedDriver,
            deliveryOrder: (result.payload && result.payload.deliveryOrder) || index + 1
          })
        }))
        const retriedById = retried.reduce((results, item) => {
          results[item.orderId] = item
          return results
        }, {})
        this.dispatchResults = this.dispatchResults.map(result => retriedById[result.orderId] || result)
        if (!this.failedDispatchResults.length) {
          this.feedback = this.$t('Inventory.dispatch_retry_success')
          this.feedbackType = 'success'
          this.$emit('completed', {
            createdOrderIds: this.createdOrderIds,
            dispatchResults: this.dispatchResults,
            allDispatchesQueued: true
          })
          this.requestClose(true)
        } else {
          this.feedback = this.$t('Inventory.dispatch_retry_error')
          this.feedbackType = 'error'
        }
      } catch (error) {
        this.feedback = error.message || this.$t('Inventory.dispatch_retry_error')
        this.feedbackType = 'error'
      } finally {
        this.submitting = false
      }
    },
    requestClose (force = false) {
      if ((this.submitting || this.failedDispatchResults.length) && !force) return
      this.$emit('input', false)
      this.$emit('close')
    },
    reset () {
      this.batchDateInput = toTorontoDateTimeInput()
      this.driverUID = ''
      this.driverSelectionTouched = false
      this.bulkInventoryLot = ''
      this.bulkInventoryTerm = ''
      this.submitting = false
      this.creationCommitted = false
      this.feedback = ''
      this.feedbackType = 'error'
      this.createdOrderIds = []
      this.dispatchResults = []
      this.buildRows()
      this.preselectInventoryDriver()
    }
  }
}
</script>

<style scoped>
.send-inventory-form {
  width: min(980px, 80vw);
  max-width: 100%;
  padding: 20px 24px 24px;
  color: #354252;
}

.send-inventory-form__toolbar,
.send-inventory-form__settings,
.send-inventory-form footer,
.send-inventory-form__retry {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.send-inventory-form__toolbar {
  padding: 13px 15px;
  margin-bottom: 14px;
  border-radius: 10px;
  background: #f3f6f8;
}

.send-inventory-form__toolbar > div {
  display: flex;
  flex-direction: column;
}

.send-inventory-form__toolbar span {
  color: #71808e;
  font-size: .82rem;
}

.send-inventory-form__select-all {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 0;
  white-space: nowrap;
}

.send-inventory-form__settings {
  align-items: flex-start;
}

.send-inventory-form__settings .form-group {
  width: 50%;
}

.send-inventory-form__bulk {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
  align-items: end;
  gap: 12px;
  padding: 13px 15px;
  margin-bottom: 14px;
  border: 1px solid #e1e7eb;
  border-radius: 10px;
  background: #f8fafb;
}

.send-inventory-form__bulk label {
  min-width: 0;
  margin: 0;
}

.send-inventory-form__bulk-apply {
  display: inline-flex;
  min-height: 38px;
  align-items: center;
  justify-content: center;
  gap: 7px;
  white-space: normal;
}

.send-inventory-form label > span,
.send-inventory-form__settings label > span {
  display: block;
  margin-bottom: 6px;
  color: #52606e;
  font-size: .82rem;
  font-weight: 700;
}

.send-inventory-form__orders {
  display: flex;
  max-height: 400px;
  flex-direction: column;
  gap: 9px;
  overflow-y: auto;
  padding: 3px;
  margin-bottom: 14px;
}

.send-inventory-order {
  display: grid;
  grid-template-columns: auto 86px minmax(0, 1fr) 210px;
  align-items: center;
  gap: 13px;
  padding: 11px;
  border: 1px solid #e2e7eb;
  border-radius: 11px;
  background: #fff;
}

.send-inventory-order--selected {
  border-color: #e8aa65;
  background: #fffbf5;
}

.send-inventory-order--disabled {
  opacity: .68;
  background: #f2f4f6;
}

.send-inventory-order__check {
  margin: 0;
}

.send-inventory-order__check input {
  width: 17px;
  height: 17px;
}

.send-inventory-order__image {
  display: flex;
  width: 86px;
  height: 64px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 8px;
  color: #8c98a3;
  background: #e6eaee;
  font-size: 1.8rem;
}

.send-inventory-order__image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.send-inventory-order__identity {
  min-width: 0;
}

.send-inventory-order__identity > div {
  display: flex;
  align-items: center;
  gap: 8px;
}

.send-inventory-order__identity > div > strong {
  color: #d97918;
  font-size: .8rem;
}

.send-inventory-order__identity h4 {
  overflow: hidden;
  margin: 3px 0;
  color: #2f3c49;
  font-size: .96rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.send-inventory-order__identity p {
  overflow-wrap: anywhere;
  margin: 0;
  color: #667482;
  font-family: monospace;
  font-size: .78rem;
}

.send-inventory-order__identity small {
  color: #7a8793;
}

.send-inventory-order__blocked {
  padding: 3px 7px;
  border-radius: 999px;
  color: #87363a;
  background: #f8dfe0;
  font-size: .68rem;
  font-weight: 700;
}

.send-inventory-order__date label {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 7px;
  font-size: .78rem;
}

.send-inventory-order__lot,
.send-inventory-order__term {
  display: block;
  margin-bottom: 10px;
}

.send-inventory-order__date small {
  color: #84909b;
}

.send-inventory-form__message,
.send-inventory-form__retry {
  padding: 12px 14px;
  margin: 12px 0;
  border-radius: 8px;
}

.send-inventory-form__message--error {
  color: #8d2f32;
  background: #fde7e7;
}

.send-inventory-form__message--success {
  color: #17613a;
  background: #e2f6e9;
}

.send-inventory-form__created {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 12px 0;
}

.send-inventory-form__created span {
  padding: 4px 9px;
  border-radius: 999px;
  background: #edf0f3;
  font-size: .78rem;
  font-weight: 700;
}

.send-inventory-form__retry {
  border: 1px solid #f0c1c1;
  color: #812e32;
  background: #fff8f8;
}

.send-inventory-form footer {
  justify-content: flex-end;
  padding-top: 16px;
  margin-top: 8px;
  border-top: 1px solid #e5e9ed;
}

@media (max-width: 800px) {
  .send-inventory-order {
    grid-template-columns: auto 70px minmax(0, 1fr);
  }

  .send-inventory-order__image {
    width: 70px;
    height: 56px;
  }

  .send-inventory-order__controls {
    grid-column: 2 / -1;
  }
}

@media (max-width: 600px) {
  .send-inventory-form {
    width: 100%;
    padding: 16px;
  }

  .send-inventory-form__toolbar,
  .send-inventory-form__settings {
    align-items: stretch;
    flex-direction: column;
  }

  .send-inventory-form__settings .form-group {
    width: 100%;
  }

  .send-inventory-form__bulk {
    grid-template-columns: 1fr;
  }

  .send-inventory-form__bulk-apply {
    width: 100%;
  }

  .send-inventory-order {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .send-inventory-order__image {
    display: none;
  }

  .send-inventory-order__controls {
    grid-column: 2;
  }

  .send-inventory-form footer {
    flex-direction: column-reverse;
  }

  .send-inventory-form footer .btn {
    width: 100%;
  }
}
</style>
